import type { RouterOutputs } from "@repo/api";
import type { CashRegisterConfigurationSchema } from "@repo/validators";
import type { z } from "zod";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import {
  CashLocationMutationType,
  ClockTypes,
  Permissions,
  ReceiptType,
} from "@repo/database";
import { capitalize, formatIntToCurrency, printLeftRight } from "@repo/utils";
import { endOfMonth, endOfWeek, format, startOfWeek } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCashLocation } from "~/hooks/use-cash-location";
import { useConfiguration } from "~/hooks/use-configuration";
import { usePlatform } from "~/hooks/use-platform";
import { usePrinter } from "~/hooks/use-printer";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { usePreferencesStore } from "~/stores/use-preferences-store";
import { useRegisterStore } from "~/stores/use-register-store";
import { usePermissionManager } from "./use-permission-manager";

export type ClosingReport =
  RouterOutputs["report"]["getClosingReports"][number];

export type MonthReport = RouterOutputs["report"]["getMonthReport"][number];
export type WeekReport = RouterOutputs["report"]["getWeekReport"][number];
export type DayReport = RouterOutputs["report"]["getDayReport"][number];

export type ReceiptReport = NonNullable<
  RouterOutputs["report"]["getReceiptReports"]
>;

export type Receipt = NonNullable<
  RouterOutputs["report"]["getReceipts"][number]
>;

export type ProductReport =
  RouterOutputs["report"]["getProductReports"][number];
const fontSize = 12;

const resetFont = (doc: jsPDF) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
};

export const useRegisterManager = () => {
  const { t } = useTranslation("common");

  const signOutAfterAction = usePreferencesStore(
    (state) => state.signOutAfterAction,
  );

  const { printers, sendToPrinter, getPrinterLanguage } = usePrinter();

  const { cashLocations } = useCashLocation();

  const { receiptSettings, revenueReportSettings } = useConfiguration();

  const { platform } = usePlatform();

  const { setDayOverviewOpen, setMutationDialogOpen, setEndDayOpen } =
    useRegisterStore();

  const { hasPermission, logout } = usePermissionManager();

  const { user } = useUser();

  const { t: userT } = useTranslation("common");

  const { mutate: updateCashRegisterConfiguration } =
    api.config.updateCashRegisterConfiguration.useMutation({
      onSuccess() {
        toast.success(
          t("update_item_success", {
            item: t("Config.cash_register_configuration"),
          }),
        );
      },
    });

  const { mutate: createMutation } =
    api.cashLocation.createMutation.useMutation();

  const { mutate: endDay } = api.report.endDay.useMutation({
    onSuccess() {
      toast.success(capitalize(t("day_closed")));
    },
  });

  const printClosingReport = (reportData: ClosingReport | ClosingReport[]) => {
    const settings = receiptSettings.find(
      (r) => r.type === ReceiptType.pro_forma,
    );

    if (!settings?.printRouteId) {
      return;
    }

    const periodStart = Array.isArray(reportData)
      ? reportData[0]!.periodStart
      : reportData.periodStart;

    const periodEnd = Array.isArray(reportData)
      ? reportData[0]!.periodEnd
      : reportData.periodEnd;

    const totals = Array.isArray(reportData)
      ? reportData.flatMap((report) => report.totals)
      : reportData.totals;

    const combinedTotals = totals.reduce<
      Record<
        string,
        {
          vatRate: string;
          gross: number;
          vat: number;
          net: number;
        }
      >
    >((acc, current) => {
      const { vatRate } = current;

      if (!acc[vatRate]) {
        // First occurrence of this group name
        acc[vatRate] = { ...current };
      } else {
        // Merge with existing entry
        acc[vatRate].gross += current.gross;
        acc[vatRate].vat += current.vat;
        acc[vatRate].net += current.net;
      }

      return acc;
    }, {});

    const totalsResult = Object.values(combinedTotals);

    const grossTotal = Array.isArray(reportData)
      ? reportData.reduce((acc, curr) => acc + curr.grossTotal, 0)
      : reportData.grossTotal;

    const vatTotal = Array.isArray(reportData)
      ? reportData.reduce((acc, curr) => acc + curr.vatTotal, 0)
      : reportData.vatTotal;

    const netTotal = Array.isArray(reportData)
      ? reportData.reduce((acc, curr) => acc + curr.netTotal, 0)
      : reportData.netTotal;

    const payments = Array.isArray(reportData)
      ? reportData.flatMap((report) => report.payments)
      : reportData.payments;

    const combinedPayments = payments.reduce<
      Record<
        string,
        {
          name: string;
          amount: number;
          tips: number;
          quantity: number;
        }
      >
    >((acc, current) => {
      const { name } = current;

      if (!acc[name]) {
        // First occurrence of this group name
        acc[name] = { ...current };
      } else {
        // Merge with existing entry
        acc[name].amount += current.amount;
        acc[name].tips += current.tips;
        acc[name].quantity += current.quantity;
      }

      return acc;
    }, {});

    const paymentsResult = Object.values(combinedPayments);

    const paymentsTotal = paymentsResult.reduce((acc, curr) => {
      return acc + curr.amount;
    }, 0);

    const selectedPrinters = printers.filter((p) =>
      p.printRoutes.includes(settings.printRouteId),
    );

    const tips = paymentsResult.reduce((acc, payment) => {
      return acc + payment.tips;
    }, 0);

    for (const printer of selectedPrinters) {
      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: printer.width,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center")
        .width(2)
        .height(2)
        .bold(true)
        .newline()
        .newline()
        .newline()
        .line(userT("Report.closing.document_title").toUpperCase())
        .width(1)
        .height(1)
        .bold(false)
        .newline()
        .line(`${format(periodStart, "Pp")} - ${format(periodEnd, "Pp")}`)
        .rule({ style: "single" })
        .newline();

      result
        .bold(true)
        .line(userT("Ticket.gross_total").toUpperCase())
        .bold(false);

      totalsResult.forEach((total) => {
        result.line(
          printLeftRight(
            `${total.vatRate}%`,
            formatIntToCurrency(total.gross),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(grossTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.vat_total").toUpperCase())
        .bold(false);

      totalsResult.forEach((total) => {
        result.line(
          printLeftRight(
            `${total.vatRate}%`,
            formatIntToCurrency(total.vat),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(vatTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.net_total").toUpperCase())
        .bold(false);

      totalsResult.forEach((total) => {
        result.line(
          printLeftRight(
            `${total.vatRate}%`,
            formatIntToCurrency(total.net),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(netTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.payment_methods").toUpperCase())
        .bold(false);

      paymentsResult
        .sort((a, b) => b.amount - a.amount)
        .forEach((payment) => {
          if (payment.amount === 0) return;
          result.line(
            printLeftRight(
              payment.name,
              formatIntToCurrency(payment.amount),
              printer.width,
            ),
          );
        });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(paymentsTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      if (tips > 0 && revenueReportSettings?.showTips !== false) {
        result.bold(true).line(userT("Ticket.tips").toUpperCase()).bold(false);
        paymentsResult
          .filter((payment) => payment.tips > 0)
          .sort((a, b) => b.tips - a.tips)
          .forEach((payment) => {
            result.line(
              printLeftRight(
                payment.name,
                formatIntToCurrency(payment.tips),
                printer.width,
              ),
            );
          });
      }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");

      const encoded = result.encode();

      void sendToPrinter(encoded, printer);
    }
  };

  const downloadClosingReport = async (
    reportData: ClosingReport | ClosingReport[],
  ) => {
    const logo = await fetch("/images/logo-3d.png");

    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait",
    });

    const periodStart = Array.isArray(reportData)
      ? reportData[0]!.periodStart
      : reportData.periodStart;

    const periodEnd = Array.isArray(reportData)
      ? reportData[reportData.length - 1]!.periodEnd
      : reportData.periodEnd;

    const turnOverGroups = Array.isArray(reportData)
      ? reportData.flatMap((report) => report.turnOverGroupTotals)
      : reportData.turnOverGroupTotals;

    const combinedTurnOverGroupTotals = turnOverGroups.reduce<
      Record<
        string,
        {
          turnoverGroupName: string;
          turnoverGroupLedgerNumber: number | undefined;
          revenue: number;
          quantity: number;
        }
      >
    >((acc, current) => {
      const { turnoverGroupName } = current;

      if (!acc[turnoverGroupName]) {
        // First occurrence of this group name
        acc[turnoverGroupName] = { ...current };
      } else {
        // Merge with existing entry
        acc[turnoverGroupName].revenue += current.revenue;
        acc[turnoverGroupName].quantity += current.quantity;

        // Keep the ledger number if it exists (optional logic)
        if (current.turnoverGroupLedgerNumber !== undefined) {
          acc[turnoverGroupName].turnoverGroupLedgerNumber =
            current.turnoverGroupLedgerNumber;
        }
      }

      return acc;
    }, {});

    const turnOverGroupsResult = Object.values(combinedTurnOverGroupTotals);

    const totals = Array.isArray(reportData)
      ? reportData.flatMap((report) => report.totals)
      : reportData.totals;

    const combinedTotals = totals.reduce<
      Record<
        string,
        {
          vatRate: string;
          gross: number;
          vat: number;
          net: number;
        }
      >
    >((acc, current) => {
      const { vatRate } = current;

      if (!acc[vatRate]) {
        // First occurrence of this group name
        acc[vatRate] = { ...current };
      } else {
        // Merge with existing entry
        acc[vatRate].gross += current.gross;
        acc[vatRate].vat += current.vat;
        acc[vatRate].net += current.net;
      }

      return acc;
    }, {});

    const totalsResult = Object.values(combinedTotals);

    const payments = Array.isArray(reportData)
      ? reportData.flatMap((report) => report.payments)
      : reportData.payments;

    const combinedPayments = payments.reduce<
      Record<
        string,
        {
          name: string;
          amount: number;
          tips: number;
          quantity: number;
        }
      >
    >((acc, current) => {
      const { name } = current;

      if (!acc[name]) {
        // First occurrence of this group name
        acc[name] = { ...current };
      } else {
        // Merge with existing entry
        acc[name].amount += current.amount;
        acc[name].tips += current.tips;
        acc[name].quantity += current.quantity;
      }

      return acc;
    }, {});

    const paymentsResult = Object.values(combinedPayments);

    const pageWidth = doc.internal.pageSize.getWidth();

    const lineHeight = fontSize * 1.4;
    let currentY = 30;
    const currentX = 20;
    doc.setFontSize(fontSize);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(t("Report.closing.document_title"), currentX, currentY);
    currentY += lineHeight;
    resetFont(doc);
    doc.setFontSize(10);
    doc.text(t("Report.company_name"), currentX, currentY);
    const chars = t("Report.company_name").length + 70;
    doc.text(user.companyName ?? "", currentX + chars, currentY);

    if (!logo.ok) {
      console.error("Could not load logo for document.");
    } else {
      const blob = await logo.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => {
        reader.onload = () => {
          const base64Image = reader.result;

          if (typeof base64Image === "string") {
            // Add the image to the PDF

            const imageWidth = 77; // Image width in points
            const imageHeight = 63; // Image height in points

            const x = pageWidth - imageWidth - currentX;

            doc.addImage(base64Image, "PNG", x, 10, imageWidth, imageHeight); // Adjust coordinates and size
          }

          resolve(true);
        };
      });
    }

    currentY += lineHeight;

    doc.text(t("Report.from"), currentX, currentY);
    doc.text(
      format(
        periodStart,
        user.clock === ClockTypes.military
          ? "dd-MM-yyyy HH:mm"
          : "MM/dd/yyyy h:mm a",
      ),
      currentX + chars,
      currentY,
    );

    currentY += lineHeight;
    doc.text(t("Report.until"), currentX, currentY);
    doc.text(
      format(
        periodEnd,
        user.clock === ClockTypes.military
          ? "dd-MM-yyyy HH:mm"
          : "MM/dd/yyyy h:mm a",
      ),
      currentX + chars,
      currentY,
    );

    currentY += lineHeight;

    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // revenue groups start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 20,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 80 },
        2: { halign: "right", cellWidth: 80 },
        3: { halign: "right", cellWidth: 100 },
        4: { halign: "right", cellWidth: 80 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general_turnover_group"),
          t("Report.general_ledger_number"),
          t("Report.general.quantity"),
          t("Report.general.amount_with_currency", { currency: "€" }),
        ],
      ],
      body: turnOverGroupsResult.map((total) => [
        total.turnoverGroupName,
        total.turnoverGroupLedgerNumber ?? "",
        formatIntToCurrency(total.quantity, false),
        formatIntToCurrency(total.revenue, false),
      ]),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 80 },
        2: { halign: "right", cellWidth: 80 },
        3: { halign: "right", cellWidth: 100 },
        4: { halign: "right", cellWidth: 80 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          "",
          formatIntToCurrency(
            turnOverGroupsResult.reduce((acc, curr) => acc + curr.quantity, 0),
            false,
          ),
          `€ ${formatIntToCurrency(
            turnOverGroupsResult.reduce((acc, curr) => acc + curr.revenue, 0),
            false,
          )}`,
        ],
      ],
    });

    // revenue groups end

    currentY += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // VAT start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + lineHeight,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },

      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general.vat_rate"),
          t("Report.general.gross_with_currency", { currency: "€" }),
          t("Report.general_vat_with_currency", { currency: "€" }),
          t("Report.general.net_with_currency", { currency: "€" }),
        ],
      ],
      body: totalsResult
        .sort((a, b) => parseInt(a.vatRate) - parseInt(b.vatRate))
        .map((total) => [
          `${formatIntToCurrency(parseInt(total.vatRate), false)}%`,
          formatIntToCurrency(total.gross, false),
          formatIntToCurrency(total.vat, false),
          formatIntToCurrency(total.net, false),
        ]),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          `€ ${formatIntToCurrency(
            totalsResult.reduce((acc, curr) => acc + curr.gross, 0),
            false,
          )}`,

          `€ ${formatIntToCurrency(
            totalsResult.reduce((acc, curr) => acc + curr.vat, 0),
            false,
          )}`,
          `€ ${formatIntToCurrency(
            totalsResult.reduce((acc, curr) => acc + curr.net, 0),
            false,
          )}`,
        ],
      ],
    });

    // VAT end

    currentY += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // payment methods start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + lineHeight,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general.payment_method"),
          t("Report.general.quantity"),
          t("Report.general.amount_with_currency", { currency: "€" }),
        ],
      ],
      body: paymentsResult
        .filter((payment) => payment.amount > 0)
        .map((payment) => [
          payment.name,
          formatIntToCurrency(payment.quantity, false),
          formatIntToCurrency(payment.amount, false),
        ]),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          `${formatIntToCurrency(
            paymentsResult.reduce((acc, curr) => acc + curr.quantity, 0),
            false,
          )}`,

          `€ ${formatIntToCurrency(
            paymentsResult.reduce((acc, curr) => acc + curr.amount, 0),
            false,
          )}`,
        ],
      ],
    });

    doc.save(
      `${t("Report.closing.pdf_file_name", { identifier: `${format(periodStart, "dd-MM-yyyy_HH:mm")} - ${format(periodEnd, "dd-MM-yyyy_HH:mm")}` })}.pdf`,
    );

    // const pdfData = doc.output("blob");
    // const handle = await window.showSaveFilePicker({
    //   suggestedName: `${t("Report.closing.pdf_file_name", { identifier: reportData.number })}.pdf`,
    //   types: [
    //     {
    //       description: "PDF File",
    //       accept: { "application/pdf": [".pdf"] },
    //     },
    //   ],
    // });
    // const writable = await handle.createWritable();
    // await writable.write(pdfData);
    // await writable.close();
  };

  const printMonthReport = (reportData: MonthReport) => {
    const settings = receiptSettings.find(
      (r) => r.type === ReceiptType.pro_forma,
    );

    if (!settings?.printRouteId) {
      return;
    }

    const selectedPrinters = printers.filter((p) =>
      p.printRoutes.includes(settings.printRouteId),
    );

    for (const printer of selectedPrinters) {
      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: printer.width,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center")
        .width(2)
        .height(2)
        .bold(true)
        .newline()
        .line(userT("Report.month_report").toUpperCase())
        .width(1)
        .height(1)
        .bold(false)
        .newline()
        .line(
          `${format(reportData.monthReport.date, "P")} - ${format(endOfMonth(reportData.monthReport.date), "P")}`,
        )
        .rule({ style: "single" })
        .newline();

      result
        .bold(true)
        .line(userT("Ticket.gross_total").toUpperCase())
        .bold(false);

      const grossTotal = Object.values(reportData.monthReport.vat).reduce(
        (acc, vat) => acc + vat,
        0,
      );

      Object.entries(reportData.monthReport.vat).forEach(([vatRate, vat]) => {
        result.line(
          printLeftRight(
            `${vatRate}%`,
            formatIntToCurrency(vat),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(grossTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.vat_total").toUpperCase())
        .bold(false);

      let vatTotal = 0;
      Object.entries(reportData.monthReport.vat).forEach(([vatRate, vat]) => {
        const rate = parseInt(vatRate);
        const total = (vat / (100 + rate)) * rate;
        vatTotal += total;
        result.line(
          printLeftRight(
            `${vatRate}%`,
            formatIntToCurrency(total),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(vatTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.net_total").toUpperCase())
        .bold(false);

      Object.entries(reportData.monthReport.vat).forEach(([vatRate, vat]) => {
        const rate = parseInt(vatRate);
        const total = vat - (vat / (100 + rate)) * rate;

        result.line(
          printLeftRight(
            `${vatRate}%`,
            formatIntToCurrency(total),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(grossTotal - vatTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      let tips = 0;
      result
        .bold(true)
        .line(userT("Ticket.payment_methods").toUpperCase())
        .bold(false);

      Object.entries(reportData.monthReport.payments)
        .sort((a, b) => b[1].amount - a[1].amount)
        .forEach(([paymentMethod, { amount, tip, quantity }]) => {
          tips += tip;
          result.line(
            printLeftRight(
              `${quantity}x ${paymentMethod}`,
              formatIntToCurrency(amount),
              printer.width,
            ),
          );
        });

      result.rule({ style: "single" });

      if (tips > 0 && revenueReportSettings?.showTips !== false) {
        result.bold(true).line(userT("Ticket.tips").toUpperCase()).bold(false);

        Object.entries(reportData.monthReport.payments)
          .filter((payment) => payment[1].tip > 0)
          .sort((a, b) => b[1].tip - a[1].tip)
          .forEach(([paymentMethod, { tip }]) => {
            result.line(
              printLeftRight(
                paymentMethod,
                formatIntToCurrency(tip),
                printer.width,
              ),
            );
          });
      }

      if (reportData.monthReport.mutations.length > 0) {
        result.rule({ style: "single" });

        result
          .bold(true)
          .line(userT("Ticket.mutations").toUpperCase())
          .bold(false);

        result.align("left");

        reportData.monthReport.mutations.forEach((mutation) => {
          result.text(`${mutation.type} - `);
          if (mutation.cashLocation) {
            result.text(`${mutation.cashLocation} | `);
          }
          if (mutation.category) {
            result.text(`${mutation.category} | `);
          }
          if (mutation.comment) {
            result.text(`${mutation.comment} | `);
          }
          result.text(`${formatIntToCurrency(mutation.value)} | `);
          result.text(`${mutation.vatRate}%`);
        });
      }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");

      const encoded = result.encode();

      void sendToPrinter(encoded, printer);
    }
  };

  const downloadMonthReport = async (reportData: MonthReport) => {
    const logo = await fetch("/images/logo-3d.png");
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait",
    });

    const pageWidth = doc.internal.pageSize.getWidth();

    const lineHeight = fontSize * 1.4;
    let currentY = 30;
    const currentX = 20;
    doc.setFontSize(fontSize);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(t("Report.month.document_title"), currentX, currentY);
    currentY += lineHeight;
    resetFont(doc);
    doc.setFontSize(10);
    doc.text(t("Report.company_name"), currentX, currentY);
    const chars = t("Report.company_name").length + 70;
    doc.text(user.companyName ?? "", currentX + chars, currentY);

    if (!logo.ok) {
      console.error("Could not load logo for document.");
    } else {
      const blob = await logo.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => {
        reader.onload = () => {
          const base64Image = reader.result;

          if (typeof base64Image === "string") {
            // Add the image to the PDF

            const imageWidth = 77; // Image width in points
            const imageHeight = 63; // Image height in points

            const x = pageWidth - imageWidth - currentX;

            doc.addImage(base64Image, "PNG", x, 10, imageWidth, imageHeight); // Adjust coordinates and size
          }

          resolve(true);
        };
      });
    }

    currentY += lineHeight;

    doc.text(t("Report.date"), currentX, currentY);
    doc.text(
      format(
        reportData.monthReport.date,
        user.clock === ClockTypes.military ? "MMMM yyyy" : "MMMM yyyy",
      ),
      currentX + chars,
      currentY,
    );

    currentY += lineHeight;

    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // revenue groups start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 20,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 80 },
        2: { halign: "right", cellWidth: 80 },
        3: { halign: "right", cellWidth: 100 },
        4: { halign: "right", cellWidth: 80 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general_turnover_group"),
          t("Report.general_ledger_number"),
          t("Report.general.quantity"),
          t("Report.general.amount_with_currency", { currency: "€" }),
        ],
      ],
      body: Object.entries(reportData.monthReport.turnOverGroupTotals)
        .filter(
          ([_turnoverGroupName, total]) =>
            total.revenue > 0 || total.quantity > 0,
        )
        .map(([turnoverGroupName, total]) => [
          turnoverGroupName,
          total.ledgerNumber ?? "",
          formatIntToCurrency(total.quantity, false),
          formatIntToCurrency(total.revenue, false),
        ]),
    });

    doc.setLineWidth(1);

    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 80 },
        2: { halign: "right", cellWidth: 80 },
        3: { halign: "right", cellWidth: 100 },
        4: { halign: "right", cellWidth: 80 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          "",
          formatIntToCurrency(reportData.monthReport.totalAmount, false),
          `€ ${formatIntToCurrency(reportData.monthReport.revenue, false)}`,
        ],
      ],
    });

    // revenue groups end

    currentY += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // VAT start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + lineHeight,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },

      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general.vat_rate"),
          t("Report.general.gross_with_currency", { currency: "€" }),
          t("Report.general_vat_with_currency", { currency: "€" }),
          t("Report.general.net_with_currency", { currency: "€" }),
        ],
      ],
      body: Object.entries(reportData.monthReport.vat).map(
        ([vatRate, vatAmount]) => [
          `${formatIntToCurrency(parseInt(vatRate), false)}%`,
          formatIntToCurrency(vatAmount, false),
          `€ ${formatIntToCurrency((vatAmount / (100 + parseInt(vatRate))) * parseInt(vatRate), false)}`,
          `€ ${formatIntToCurrency(vatAmount - (vatAmount / (100 + parseInt(vatRate))) * parseInt(vatRate), false)}`,
        ],
      ),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          `€ ${formatIntToCurrency(reportData.monthReport.revenue, false)}`,
          `€ ${formatIntToCurrency(
            Object.entries(reportData.monthReport.vat).reduce(
              (acc, [vatRate, vatAmount]) => {
                const rate = parseInt(vatRate);
                const total = (vatAmount / (100 + rate)) * rate;
                return acc + total;
              },
              0,
            ),
            false,
          )}`,
          `€ ${formatIntToCurrency(
            reportData.monthReport.revenue -
              Object.entries(reportData.monthReport.vat).reduce(
                (acc, [vatRate, vatAmount]) => {
                  const rate = parseInt(vatRate);
                  const total = (vatAmount / (100 + rate)) * rate;
                  return acc + total;
                },
                0,
              ),
            false,
          )}`,
        ],
      ],
    });

    // VAT end

    currentY += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // payment methods start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + lineHeight,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general.payment_method"),
          t("Report.general.quantity"),
          t("Report.general.amount_with_currency", { currency: "€" }),
        ],
      ],
      body: Object.entries(reportData.monthReport.payments).map(
        ([paymentMethod, payment]) => [
          paymentMethod,
          formatIntToCurrency(payment.quantity, false),
          formatIntToCurrency(payment.amount, false),
        ],
      ),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          `${formatIntToCurrency(
            Object.entries(reportData.monthReport.payments)
              .map(
                ([_paymentMethod, payment]) => payment.quantity, // Sum all payment quantities
              )
              .reduce((acc, curr) => acc + curr, 0),
            false,
          )}`,

          `€${formatIntToCurrency(
            Object.entries(reportData.monthReport.payments)
              .map(
                ([_paymentMethod, payment]) => payment.amount, // Sum all payment quantities
              )
              .reduce((acc, curr) => acc + curr, 0),
            false,
          )}`,
        ],
      ],
    });

    // return new Blob([doc.output("arraybuffer")], { type: "application/pdf" });

    doc.save(
      `${t("Report.month.pdf_file_name", { month: format(reportData.monthReport.date, "LLLL yyyy") })}.pdf`,
    );
  };

  const downloadWeekReport = async (reportData: WeekReport) => {
    const logo = await fetch("/images/logo-3d.png");
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait",
    });

    const pageWidth = doc.internal.pageSize.getWidth();

    const lineHeight = fontSize * 1.4;
    let currentY = 30;
    const currentX = 20;
    doc.setFontSize(fontSize);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(t("Report.week.document_title"), currentX, currentY);
    currentY += lineHeight;
    resetFont(doc);
    doc.setFontSize(10);
    doc.text(t("Report.company_name"), currentX, currentY);
    const chars = t("Report.company_name").length + 70;
    doc.text(user.companyName ?? "", currentX + chars, currentY);

    if (!logo.ok) {
      console.error("Could not load logo for document.");
    } else {
      const blob = await logo.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => {
        reader.onload = () => {
          const base64Image = reader.result;

          if (typeof base64Image === "string") {
            // Add the image to the PDF

            const imageWidth = 77; // Image width in points
            const imageHeight = 63; // Image height in points

            const x = pageWidth - imageWidth - currentX;

            doc.addImage(base64Image, "PNG", x, 10, imageWidth, imageHeight); // Adjust coordinates and size
          }

          resolve(true);
        };
      });
    }

    currentY += lineHeight;

    doc.text(t("Report.date"), currentX, currentY);
    doc.text(
      `${format(startOfWeek(reportData.date), "dd-MM-yyyy")} / ${format(endOfWeek(reportData.date), "dd-MM-yyyy")}`,
      currentX + chars,
      currentY,
    );

    currentY += lineHeight;

    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // revenue groups start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 20,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 80 },
        2: { halign: "right", cellWidth: 80 },
        3: { halign: "right", cellWidth: 100 },
        4: { halign: "right", cellWidth: 80 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general_turnover_group"),
          t("Report.general_ledger_number"),
          t("Report.general.quantity"),
          t("Report.general.amount_with_currency", { currency: "€" }),
        ],
      ],
      body: Object.entries(reportData.turnOverGroupTotals)
        .filter(
          ([_turnoverGroupName, total]) =>
            total.revenue > 0 || total.quantity > 0,
        )
        .map(([turnoverGroupName, total]) => [
          turnoverGroupName,
          total.ledgerNumber ?? "",
          formatIntToCurrency(total.quantity, false),
          formatIntToCurrency(total.revenue, false),
        ]),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 80 },
        2: { halign: "right", cellWidth: 80 },
        3: { halign: "right", cellWidth: 100 },
        4: { halign: "right", cellWidth: 80 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          "",
          formatIntToCurrency(reportData.totalAmount, false),
          `€ ${formatIntToCurrency(reportData.revenue, false)}`,
        ],
      ],
    });

    // revenue groups end

    currentY += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // VAT start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + lineHeight,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },

      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general.vat_rate"),
          t("Report.general.gross_with_currency", { currency: "€" }),
          t("Report.general_vat_with_currency", { currency: "€" }),
          t("Report.general.net_with_currency", { currency: "€" }),
        ],
      ],
      body: Object.entries(reportData.vat).map(([vatRate, vatAmount]) => [
        `${formatIntToCurrency(parseInt(vatRate), false)}%`,
        formatIntToCurrency(vatAmount, false),
        `€ ${formatIntToCurrency((vatAmount / (100 + parseInt(vatRate))) * parseInt(vatRate), false)}`,
        `€ ${formatIntToCurrency(vatAmount - (vatAmount / (100 + parseInt(vatRate))) * parseInt(vatRate), false)}`,
      ]),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          `€ ${formatIntToCurrency(reportData.revenue, false)}`,
          `€ ${formatIntToCurrency(
            Object.entries(reportData.vat).reduce(
              (acc, [vatRate, vatAmount]) => {
                const rate = parseInt(vatRate);
                const total = (vatAmount / (100 + rate)) * rate;
                return acc + total;
              },
              0,
            ),
            false,
          )}`,
          `€ ${formatIntToCurrency(
            reportData.revenue -
              Object.entries(reportData.vat).reduce(
                (acc, [vatRate, vatAmount]) => {
                  const rate = parseInt(vatRate);
                  const total = (vatAmount / (100 + rate)) * rate;
                  return acc + total;
                },
                0,
              ),
            false,
          )}`,
        ],
      ],
    });

    // VAT end

    currentY += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    // payment methods start

    autoTable(doc, {
      margin: currentX,
      startY: currentY + lineHeight,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      // Ensure column styles apply to all sections
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      head: [
        [
          t("Report.general.payment_method"),
          t("Report.general.quantity"),
          t("Report.general.amount_with_currency", { currency: "€" }),
        ],
      ],
      body: Object.entries(reportData.payments).map(
        ([paymentMethod, payment]) => [
          paymentMethod,
          formatIntToCurrency(payment.quantity, false),
          formatIntToCurrency(payment.amount, false),
        ],
      ),
    });

    doc.setLineWidth(1);
    doc.line(currentX, currentY, pageWidth - currentX, currentY);

    autoTable(doc, {
      margin: currentX,
      startY: currentY + 2,
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "right", cellWidth: 130 },
        2: { halign: "right", cellWidth: 130 },
        3: { halign: "right", cellWidth: 130 },
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          currentY = data.cursor.y;
        }
      },
      didParseCell: function (data) {
        const col = data.column.index;
        if (col === 0) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "right";
        }
      },
      body: [
        [
          t("Report.general.total"),
          `${formatIntToCurrency(
            Object.entries(reportData.payments)
              .map(
                ([_paymentMethod, payment]) => payment.quantity, // Sum all payment quantities
              )
              .reduce((acc, curr) => acc + curr, 0),
            false,
          )}`,

          `€${formatIntToCurrency(
            Object.entries(reportData.payments)
              .map(
                ([_paymentMethod, payment]) => payment.amount, // Sum all payment quantities
              )
              .reduce((acc, curr) => acc + curr, 0),
            false,
          )}`,
        ],
      ],
    });

    doc.save(
      `${t("Report.week.pdf_file_name", { week: `${format(startOfWeek(reportData.date), "dd-MM-yyyy")}/${format(endOfWeek(reportData.date), "dd-MM-yyyy")}` })}.pdf`,
    );
  };

  const printDayReport = (reportData: DayReport) => {
    const settings = receiptSettings.find(
      (r) => r.type === ReceiptType.pro_forma,
    );

    if (!settings?.printRouteId) {
      return;
    }

    const selectedPrinters = printers.filter((p) =>
      p.printRoutes.includes(settings.printRouteId),
    );

    for (const printer of selectedPrinters) {
      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: printer.width,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center")
        .width(2)
        .height(2)
        .bold(true)
        .newline()
        .line(userT("Report.day_report").toUpperCase())
        .width(1)
        .height(1)
        .bold(false)
        .newline()
        .line(`${format(reportData.date, "P")}`)
        .rule({ style: "single" })
        .newline();

      result
        .bold(true)
        .line(userT("Ticket.gross_total").toUpperCase())
        .bold(false);

      if (reportData.vat) {
        const grossTotal = Object.values(reportData.vat).reduce(
          (acc, vat) => acc + vat,
          0,
        );

        Object.entries(reportData.vat).forEach(([vatRate, vat]) => {
          result.line(
            printLeftRight(
              `${vatRate}%`,
              formatIntToCurrency(vat),
              printer.width,
            ),
          );
        });

        result
          .bold(true)
          .line(
            printLeftRight(
              userT("Ticket.total"),
              formatIntToCurrency(grossTotal),
              printer.width,
            ),
          )
          .bold(false);

        result.rule({ style: "single" });

        result
          .bold(true)
          .line(userT("Ticket.vat_total").toUpperCase())
          .bold(false);

        let vatTotal = 0;
        Object.entries(reportData.vat).forEach(([vatRate, vat]) => {
          const rate = parseInt(vatRate);
          const total = (vat / (100 + rate)) * rate;
          vatTotal += total;
          result.line(
            printLeftRight(
              `${vatRate}%`,
              formatIntToCurrency(total),
              printer.width,
            ),
          );
        });

        result
          .bold(true)
          .line(
            printLeftRight(
              userT("Ticket.total"),
              formatIntToCurrency(vatTotal),
              printer.width,
            ),
          )
          .bold(false);

        result.rule({ style: "single" });

        result
          .bold(true)
          .line(userT("Ticket.net_total").toUpperCase())
          .bold(false);

        Object.entries(reportData.vat).forEach(([vatRate, vat]) => {
          const rate = parseInt(vatRate);
          const total = vat - (vat / (100 + rate)) * rate;

          result.line(
            printLeftRight(
              `${vatRate}%`,
              formatIntToCurrency(total),
              printer.width,
            ),
          );
        });

        result
          .bold(true)
          .line(
            printLeftRight(
              userT("Ticket.total"),
              formatIntToCurrency(grossTotal - vatTotal),
              printer.width,
            ),
          )
          .bold(false);

        result.rule({ style: "single" });

        if (reportData.payments) {
          let tips = 0;
          result
            .bold(true)
            .line(userT("Ticket.payment_methods").toUpperCase())
            .bold(false);

          Object.entries(reportData.payments)
            .sort((a, b) => b[1].amount - a[1].amount)
            .forEach(([paymentMethod, { amount, tip, quantity }]) => {
              if (amount === 0) return;
              tips += tip;
              result.line(
                printLeftRight(
                  `${quantity}x ${paymentMethod}`,
                  formatIntToCurrency(amount),
                  printer.width,
                ),
              );
            });

          result.rule({ style: "single" });

          if (tips > 0 && revenueReportSettings?.showTips !== false) {
            result
              .bold(true)
              .line(userT("Ticket.tips").toUpperCase())
              .bold(false);

            Object.entries(reportData.payments)
              .filter((payment) => payment[1].tip > 0)
              .sort((a, b) => b[1].tip - a[1].tip)
              .forEach(([paymentMethod, { tip }]) => {
                result.line(
                  printLeftRight(
                    paymentMethod,
                    formatIntToCurrency(tip),
                    printer.width,
                  ),
                );
              });
          }
        }
      }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");

      const encoded = result.encode();

      void sendToPrinter(encoded, printer);
    }
  };

  const printWeekReport = (reportData: WeekReport) => {
    const settings = receiptSettings.find(
      (r) => r.type === ReceiptType.pro_forma,
    );

    if (!settings?.printRouteId) {
      return;
    }

    const selectedPrinters = printers.filter((p) =>
      p.printRoutes.includes(settings.printRouteId),
    );

    for (const printer of selectedPrinters) {
      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: printer.width,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center")
        .width(2)
        .height(2)
        .bold(true)
        .newline()
        .line(userT("Report.week_report").toUpperCase())
        .width(1)
        .height(1)
        .bold(false)
        .newline()
        .line(
          `${format(reportData.date, "P")} - ${format(endOfWeek(reportData.date), "P")}`,
        )
        .rule({ style: "single" })
        .newline();

      result
        .bold(true)
        .line(userT("Ticket.gross_total").toUpperCase())
        .bold(false);

      const grossTotal = Object.values(reportData.vat).reduce(
        (acc, vat) => acc + vat,
        0,
      );

      Object.entries(reportData.vat).forEach(([vatRate, vat]) => {
        result.line(
          printLeftRight(
            `${vatRate}%`,
            formatIntToCurrency(vat),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(grossTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.vat_total").toUpperCase())
        .bold(false);

      let vatTotal = 0;
      Object.entries(reportData.vat).forEach(([vatRate, vat]) => {
        const rate = parseInt(vatRate);
        const total = (vat / (100 + rate)) * rate;
        vatTotal += total;
        result.line(
          printLeftRight(
            `${vatRate}%`,
            formatIntToCurrency(total),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(vatTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      result
        .bold(true)
        .line(userT("Ticket.net_total").toUpperCase())
        .bold(false);

      Object.entries(reportData.vat).forEach(([vatRate, vat]) => {
        const rate = parseInt(vatRate);
        const total = vat - (vat / (100 + rate)) * rate;

        result.line(
          printLeftRight(
            `${vatRate}%`,
            formatIntToCurrency(total),
            printer.width,
          ),
        );
      });

      result
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(grossTotal - vatTotal),
            printer.width,
          ),
        )
        .bold(false);

      result.rule({ style: "single" });

      let tips = 0;
      result
        .bold(true)
        .line(userT("Ticket.payment_methods").toUpperCase())
        .bold(false);

      Object.entries(reportData.payments)
        .sort((a, b) => b[1].amount - a[1].amount)
        .forEach(([paymentMethod, { amount, tip, quantity }]) => {
          if (amount === 0) return;
          tips += tip;
          result.line(
            printLeftRight(
              `${quantity}x ${paymentMethod}`,
              formatIntToCurrency(amount),
              printer.width,
            ),
          );
        });

      result.rule({ style: "single" });

      if (tips > 0 && revenueReportSettings?.showTips !== false) {
        result.bold(true).line(userT("Ticket.tips").toUpperCase()).bold(false);

        Object.entries(reportData.payments)
          .filter((payment) => payment[1].tip > 0)
          .sort((a, b) => b[1].tip - a[1].tip)
          .forEach(([paymentMethod, { tip }]) => {
            result.line(
              printLeftRight(
                paymentMethod,
                formatIntToCurrency(tip),
                printer.width,
              ),
            );
          });
      }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");

      const encoded = result.encode();

      void sendToPrinter(encoded, printer);
    }
  };

  const openDayOverview = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.day_overview,
    });

    if (!permission) return;

    setDayOverviewOpen(true);
  };

  const openDrawer = async () => {
    if (platform !== "DESKTOP") return;

    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.open_drawer,
    });

    if (!permission) return;

    const printer = printers.find((p) => p.hasCashDrawer === true);

    if (!printer) return;

    const encoder = new ReceiptPrinterEncoder({
      language: getPrinterLanguage(printer.language),
      columns: printer.width,
    });

    const result = encoder.pulse().encode();

    void sendToPrinter(result, printer);

    const cashLocation = cashLocations.find((obj) => obj.type === "drawer");

    if (!cashLocation) return;

    if (signOutAfterAction) {
      logout();
    }

    createMutation({
      cashLocationId: cashLocation.id,
      type: CashLocationMutationType.open_drawer,
      vatRate: 0,
      value: 0,
      employeeId: employee?.id,
    });
  };

  const openDrawerMutation = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_mutation,
    });

    if (!permission) return;

    setMutationDialogOpen(true);
  };

  const updateCashRegisterConfigurationHandler = async (
    data: z.infer<ReturnType<typeof CashRegisterConfigurationSchema>>,
  ) => {
    return new Promise<boolean>((resolve) => {
      updateCashRegisterConfiguration(data, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const openEndDay = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.close_drawer,
    });

    if (!permission) return;

    setEndDayOpen(true);
  };

  const endDayHandler = async (periodStart?: Date) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.close_drawer,
    });

    return new Promise<string | null>((resolve) => {
      if (!permission) return resolve(null);
      endDay(periodStart, {
        onSuccess: (result) => {
          resolve(result);
        },
        onError: () => {
          resolve(null);
        },
      });
    });
  };

  return {
    updateCashRegisterConfiguration: updateCashRegisterConfigurationHandler,
    printClosingReport,
    downloadClosingReport,
    downloadWeekReport,
    downloadMonthReport,
    printMonthReport,
    printDayReport,
    printWeekReport,
    openDrawerMutation,
    openDayOverview,
    openDrawer,
    openEndDay,
    endDay: endDayHandler,
  };
};

export const useRegisterUpdates = () => {};
